import { useEffect, useState } from 'react'
import { FormikProvider } from "formik"
import { isEmpty } from 'lodash'
import * as yup from 'yup'
import { Autocomplete, Box, CircularProgress, Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Dialog from 'components/dialog/dialog'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import FORMIK from 'modules/formik'
import { useCreateListBannerMutation, useGetEventDropdownMutation } from 'store/serviceBookingEventBanner'
import type { PartialBannerCreateDataProps, PartialBannerCreateProps } from 'modules/partial'
import type {
    CallbackHandler, DetailStateProps,
    ErrorProps
} from 'modules/types'
import GlobalStyle from 'modules/styles'
import SegmentationFormFields from "components/segmentation"
import { buildSegmentationJson, SEGMENTATION_DELETE_FIELDS, segmentationSchemaShape } from "components/segmentation/utils"
import ImageBox from "components/ImageBox"
import { BANNER_PLACEMENT_POLICY } from 'pages/campaign/event/serviceBooking/banner/config'
import type { BannerView } from 'pages/campaign/event/serviceBooking/banner/config'
import DialogStyle from './style'

const Create = ({ open, onClose, view, showEventSelector }: DetailStateProps & {
    view: BannerView
    showEventSelector: boolean
}) => {
    const [createBannerList, create] = useCreateListBannerMutation()
    if (!open) return <></>

    const placement = view.placement

    const initialValues: PartialBannerCreateProps = {
        Image: {} as File,
        data: {
            bannerImage: '',
            Placement: placement,
            ValidFrom: '',
            ValidUntil: '',
            targetOptions: [],
            Status: '',
            memberTiers: [],
            activeStatus: '',
            bluelinkStatus: '',
            licensePlateType: '',
            locations: [],
            carOwnership: [],
            salutation: '',
            vinCsvFile: null,
            memberIdCsvFile: null,
            vinCsvFileUrl: '',
            memberIdCsvFileUrl: '',
            CarModelYears: [],
            handoverDateRange: { minDate: '', maxDate: '' },
            Order: 0,
            ...(view.kind === 'banner' ? {
                Title: '',
                Url: ''
            } : undefined)
        }
    }

    function handleSubmit(values: PartialBannerCreateProps) {
        const formData = new FormData()

        const hasSegmentation = BANNER_PLACEMENT_POLICY[placement].segmentation
        const requestData: Record<string, unknown> = { ...values.data }

        requestData.Placement = placement
        if (!values.data.EventId) delete requestData.EventId

        if (hasSegmentation) {
            requestData.segmentationType = Array.isArray(values.data.targetOptions)
                ? values.data.targetOptions.join(', ')
                : values.data.targetOptions
            requestData.segmentationValue = buildSegmentationJson(values.data)
        } else {
            delete requestData.targetOptions
            delete requestData.segmentationType
            delete requestData.segmentationValue
        }
        SEGMENTATION_DELETE_FIELDS.forEach((field) => delete requestData[field])

        if (view.kind === 'service-event' ? values.Image : values.Image instanceof File) {
            formData.append('Image', values.Image)
        }
        if (hasSegmentation && values.data.vinCsvFile) formData.append('file', values.data.vinCsvFile)
        if (hasSegmentation && values.data.memberIdCsvFile) formData.append('file', values.data.memberIdCsvFile)
        formData.append('data', JSON.stringify(requestData))
        createBannerList(formData)
    }

    return (
        <>
            <BannerDialogForm
                open={open}
                onClose={onClose}
                loading={create.isLoading}
                view={view}
                showEventSelector={showEventSelector}
                initialValues={initialValues}
                onSubmit={handleSubmit} />

            <Notification
                open={!create.isLoading && !create.isUninitialized}
                onClose={() => (create.isError ? create.reset() : location.reload())}
                isError={Boolean(create.error) && create.isError}
                message={GLOBAL.returnExceptionMessage(create.isError, create.error as ErrorProps)}
            />
        </>
    )
}

export default Create

export function BannerDialogForm({
    open,
    onClose,
    loading,
    fetching = false,
    initialValues,
    onSubmit,
    view,
    showEventSelector
}: {
    open: boolean
    onClose: () => void
    loading: boolean
    fetching?: boolean
    view: BannerView
    showEventSelector: boolean
    initialValues: PartialBannerCreateProps,
    onSubmit: (values: PartialBannerCreateProps) => void
}) {
    const placement = view.placement
    const isCustomerCare = view.kind === 'banner' && placement === 'CustomerCare'
    const hasSegmentation = BANNER_PLACEMENT_POLICY[placement].segmentation

    const scheme = yup.object<PartialBannerCreateProps>({
        Image: initialValues.data.bannerImage ? undefined : yup.string().required('BannerImage is required'),
        data: yup.object<PartialBannerCreateDataProps>({
            ...(!initialValues.data.id
                ? { Placement: yup.string().required('Placement is required') }
                : undefined),
            ...(view.kind === 'banner' ? { Title: yup.string().required('Title is required') } : undefined),
            ...(!isCustomerCare ? {
                Order: yup.number().nullable().default(0),
                ValidFrom: yup.string().required('Period start is required')
                    .test('is-date-valid', 'start date must be earlier than end date', (val, ctx) => dayjs(val).isBefore(ctx.parent.ValidUntil)),
                ValidUntil: yup.string().required('Period end is required')
                    .test('is-date-valid', 'end date must be later than start date', (val, ctx) => dayjs(val).isAfter(ctx.parent.ValidFrom)),
                Status: yup.string().required('Status Type is required')
            } : undefined),
            ...(hasSegmentation ? segmentationSchemaShape : undefined)
        })
    })

    const formik = FORMIK.useFormFormik<PartialBannerCreateProps>(
        scheme,
        initialValues,
        onSubmit
    )

    const [getEventDropdown, eventDropdown] = useGetEventDropdownMutation()
    const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>(null)
    useEffect(() => {
        if (showEventSelector) {
            getEventDropdown().unwrap()
        }

        if (initialValues?.data?.bannerImage) {
            setPreviewImage(initialValues.data.bannerImage)
        }
    }, [getEventDropdown, initialValues?.data, showEventSelector])


    const fileOnChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: string,
        callback: CallbackHandler<string | ArrayBuffer | null>
    ) => {
        formik.setFieldError('data.bannerImage', undefined)
        const files = e && e.target && e.target.files
        const notEmpty = files && files.length > 0 && !isEmpty(files)

        if (notEmpty) {
            if (files[0].size > 200 * 1000) {
                formik.setFieldError('data.bannerImage', '* Maximum file size is 200kb')
                e.target.value = '';
                return
            }
            const reader = new FileReader()

            reader.onload = () => callback(reader.result)
            reader.readAsDataURL(files[0])
            formik.setFieldValue('data.bannerImage', files[0].name)
            formik.setFieldValue(field, files[0])
            return
        }

        formik.setFieldValue(field, {} as File)
        return callback(null)
    }

    const handleChangeDate = (field: string) => (e: Dayjs | null) => {
        const newDate = (!isEmpty(e) && e?.format('YYYY/MM/DD HH:mm:ss')) || ''
        formik.setFieldValue(field, newDate)
    }

    const error = formik.errors && (formik.errors.data as unknown as PartialBannerCreateDataProps)
    const touched = formik.touched && (formik.touched as PartialBannerCreateDataProps)
    const requestToken = formik.values.data


    return (
        <Dialog
            title={fetching ? 'Loading...' : initialValues.data.id ? 'Edit Banner' : 'Create Banner'}
            open={open}
            onCancel={onClose}
            onSubmit={() => formik.handleSubmit()}
            loading={loading}
            isDisabled={!formik.isValid}
        >
            {fetching && <CircularProgress color='inherit' />}
            {!fetching && <FormikProvider value={formik}>
                <Container {...DialogStyle.Container}>
                    {showEventSelector ? (
                        <Autocomplete
                            options={(eventDropdown && eventDropdown.data) || []}
                            value={eventDropdown.data?.find(e => e.value == formik.values.data.EventId) || null}
                            getOptionLabel={(list) => list.text}
                            isOptionEqualToValue={(option, value) =>
                                option && value ? option.value == value.value : false
                            }
                            onOpen={() => getEventDropdown()}
                            onChange={(_, id) => formik.setFieldValue('data.EventId', id && id.value)}
                            ListboxProps={GlobalStyle.ListBox}
                            renderOption={(props, item) => (
                                <li {...props} key={item.value}>
                                    {item.text}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name='EventId'
                                    label='Event'
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {eventDropdown.isLoading && (
                                                    <CircularProgress color='inherit' size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    ) : null}
                    {view.kind === 'banner' ? (
                        <>
                            <TextField
                                id='Title'
                                variant='outlined'
                                label='Title'
                                value={formik.values.data.Title}
                                onChange={(event) => formik.setFieldValue('data.Title', event.target.value)}
                                onBlur={formik.handleBlur}
                                error={touched && Boolean(touched.Title) && error && Boolean(error.Title)}
                                helperText={touched && touched.Title && error && error.Title}
                                fullWidth
                            />
                            {!isCustomerCare && (
                                <TextField
                                    id='Url'
                                    variant='outlined'
                                    label='Url'
                                    value={formik.values.data.Url}
                                    onChange={(event) => formik.setFieldValue('data.Url', event.target.value)}
                                    onBlur={formik.handleBlur}
                                    error={touched && Boolean(touched.Url) && error && Boolean(error.Url)}
                                    helperText={touched && touched.Url && error && error.Url}
                                    fullWidth
                                />
                            )}</>
                    ) : null}
                    <TextField
                        id='Image'
                        label='Banner Image'
                        type='file'
                        placeholder='Image'
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            fileOnChange(e, 'Image', (value) => setPreviewImage(value))
                        }
                        onBlur={formik.handleBlur}
                        fullWidth
                    />
                    {formik.errors.data?.bannerImage && (
                        <FormHelperText error id='image'>
                            {formik.errors.data?.bannerImage}
                        </FormHelperText>
                    )}
                    {previewImage && (
                        <Grid item xs={12}>
                            <ImageBox
                                src={previewImage as string}
                                alt='Banner Image'
                                label='Banner Image'
                            />
                        </Grid>
                    )}
                    {!isCustomerCare && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                                <DateTimePicker
                                    value={requestToken && requestToken.ValidFrom ? dayjs(requestToken.ValidFrom) : undefined}
                                    onChange={handleChangeDate('data.ValidFrom')}
                                    onClose={() => {
                                        formik.handleBlur({ target: { name: 'ValidFrom' } })
                                        formik.validateField('data.ValidFrom')
                                        formik.validateField('data.ValidUntil')
                                    }}
                                    label='Airing Period Start Date'
                                    format='DD-MM-YYYY HH:mm'
                                    sx={{ width: '100%' }}
                                    slotProps={{ textField: touched.ValidFrom ? { error: Boolean(error?.ValidFrom), helperText: error?.ValidFrom } : undefined }}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                                <DateTimePicker
                                    value={requestToken && requestToken.ValidUntil ? dayjs(requestToken.ValidUntil) : undefined}
                                    onChange={handleChangeDate('data.ValidUntil')}
                                    onClose={() => {
                                        formik.handleBlur({ target: { name: 'ValidUntil' } })
                                        formik.validateField('data.ValidFrom')
                                        formik.validateField('data.ValidUntil')
                                    }}
                                    label='Airing Period End Date'
                                    format='DD-MM-YYYY HH:mm'
                                    sx={{ width: '100%' }}
                                    slotProps={{ textField: touched.ValidUntil ? { error: Boolean(error?.ValidUntil), helperText: error?.ValidUntil } : undefined }}
                                />
                                <FormHelperText error id='airingDate'>
                                    * If current date is not within Period, Banner will not be shown on the app.
                                </FormHelperText>
                            </LocalizationProvider>
                        </Box>
                    )}
                    {hasSegmentation && <SegmentationFormFields prefix='data' />}
                    {!isCustomerCare && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id='banner-status-label'>Status</InputLabel>
                                <Select
                                    labelId='banner-status-label'
                                    id='Status'
                                    variant='outlined'
                                    label='Status'
                                    value={formik.values.data.Status}
                                    onChange={(event) => {
                                        formik.setFieldValue('data.Status', '')
                                        formik.setFieldValue('data.Status', event.target.value)
                                    }}
                                    error={
                                        touched && Boolean(touched.Status) && error && Boolean(error.Status)
                                    }
                                >
                                    <MenuItem value='Enabled'>Enabled</MenuItem>
                                    <MenuItem value='Disabled'>Disabled</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                id='Order'
                                variant='outlined'
                                label='Sequence'
                                value={formik.values.data.Order}
                                onChange={(event) => formik.setFieldValue('data.Order', event.target.value)}
                                onBlur={formik.handleBlur}
                                error={touched && Boolean(touched.Order) && error && Boolean(error.Order)}
                                helperText={touched && touched.Order && error && error.Order}
                                fullWidth
                            />
                        </Box>
                    )}
                </Container>
            </FormikProvider>}
        </Dialog>
    )
}
